import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Lightbulb, AlertTriangle, PackageSearch, Clock, ChevronDown } from "lucide-react";

const SEV = {
  high: "text-red-700 ring-red-200",
  medium: "text-amber-700 ring-amber-200",
  low: "text-emerald-700 ring-emerald-200",
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export function AiInsightsCard() {
  const { activeSector } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Günlük otomatik: her açılışta yüklenir; backend gün başına bir kez üretir (elle yenileme yok).
  // Aktif sektör değişince yeniden yüklenir — sektöre özel öngörü.
  useEffect(() => {
    setLoading(true);
    api.get("/ai/insights", { timeout: 30000 })
      .then((r) => setData(r.data))
      .catch(() => setData({ summary: "Öngörü şu an hazırlanıyor — birazdan otomatik görünecek. Sayfayı yenileyebilirsiniz.", alerts: [] }))
      .finally(() => setLoading(false));
  }, [activeSector]);
  return (
    <div className="rounded-md bg-gradient-to-br from-brand-green to-emerald-900 p-4 text-white shadow-sm" data-testid="ai-insights-card">
      <div className="mb-2 flex items-center gap-2">
        <Lightbulb className="h-4 w-4 text-amber-300" />
        <p className="font-heading text-sm font-bold">AI Günlük Öngörü</p>
        <span className="ml-auto rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-white/70" data-testid="ai-insights-auto">otomatik · günlük</span>
      </div>
      {loading && !data ? (
        <div className="flex items-center gap-2 py-3 text-sm text-white/70"><Loader2 className="h-4 w-4 animate-spin" /> Analiz ediliyor…</div>
      ) : (
        <>
          <p className="text-sm leading-relaxed text-white/90">{data?.summary || "Özet üretilemedi."}</p>
          {(data?.alerts || []).length > 0 && (
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {data.alerts.map((a, i) => (
                <div key={i} data-testid={`ai-alert-${i}`} className={`rounded-md bg-white p-2.5 ring-1 ring-inset ${SEV[a.severity] || SEV.low}`}>
                  <p className="flex items-center gap-1.5 text-xs font-bold"><AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {a.title}</p>
                  <p className="mt-0.5 text-[11px] text-slate-600">{a.detail}</p>
                </div>
              ))}
            </div>
          )}
          {data?.generated_at && (
            <p data-testid="ai-insights-generated" className="mt-3 flex items-center gap-1 text-[11px] text-white/60">
              <Clock className="h-3 w-3" /> Bugün {fmtTime(data.generated_at)}'de otomatik üretildi
            </p>
          )}
        </>
      )}
    </div>
  );
}

export function ReorderCard() {
  const { activeSector } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get("/ai/reorder", { timeout: 30000 })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], note: "" }))
      .finally(() => setLoading(false));
  }, [activeSector]);
  const items = data?.items || [];
  const LIMIT = 5;
  const shown = showAll ? items : items.slice(0, LIMIT);
  return (
    <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="ai-reorder-card">
      <div className="mb-3 flex items-center gap-2">
        <PackageSearch className="h-4 w-4 text-brand-orange" />
        <p className="font-heading text-sm font-bold text-slate-800">AI Sipariş Önerisi</p>
        <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500" data-testid="ai-reorder-auto">otomatik · günlük</span>
      </div>
      {loading && !data ? (
        <div className="flex items-center gap-2 py-3 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Hesaplanıyor…</div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400">Şimdilik acil sipariş önerisi yok 👍</p>
      ) : (
        <>
          {data?.note && <p className="mb-2 rounded-md bg-orange-50 px-2.5 py-1.5 text-[11px] text-orange-700 ring-1 ring-inset ring-orange-200">{data.note}</p>}
          <div className="space-y-1.5">
            {shown.map((it, i) => (
              <div key={i} data-testid={`reorder-row-${i}`} className="flex items-center gap-2 rounded-sm bg-slate-50 px-2.5 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800">{it.name}</p>
                  <p className="text-[11px] text-slate-400">Stok {it.current_stock} {it.unit} · günlük ~{it.daily_avg} · {it.days_left != null ? `~${it.days_left} gün kaldı` : "yavaş devir"}</p>
                </div>
                <span className="shrink-0 rounded-full bg-brand-orange px-2.5 py-1 text-xs font-bold text-white tabular">+{it.suggested_qty} {it.unit}</span>
              </div>
            ))}
          </div>
          {items.length > LIMIT && (
            <button data-testid="ai-reorder-showall" onClick={() => setShowAll((v) => !v)}
              className="mt-2 flex w-full items-center justify-center gap-1 rounded-md bg-slate-50 py-1.5 text-xs font-semibold text-brand-orange ring-1 ring-inset ring-slate-200 transition-colors hover:bg-orange-50">
              {showAll ? "Daha az göster" : `Tümünü göster (${items.length})`}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAll ? "rotate-180" : ""}`} />
            </button>
          )}
        </>
      )}
    </div>
  );
}
